import { useCallback, useEffect, useRef, useState } from "react"

import { SCROLL_PAGE_SIZE } from "../../../redux/shared/paged-list.ts"
import { extractParamsFromNextUrl } from "../../../utils/form/extract-params-from-next-url.ts"
import { getTracking } from "../api/tracking.services.ts"
import type { TrackedEnvelope, TrackingQuery } from "../types/tracking.types.ts"

interface ListState {
  items: TrackedEnvelope[]
  count: number
  next: string | null
  retryNext: string | null
  loading: boolean
  failed: boolean
}

// An answer that is not a page (a proxy's HTML, an error body) is a failed load, never a crash.
const isPage = (
  data: unknown
): data is {
  results: TrackedEnvelope[]
  count: number
  next?: string | null
} => Boolean(data) && Array.isArray((data as { results?: unknown }).results)

const STARTING: ListState = {
  items: [],
  count: 0,
  next: null,
  retryNext: null,
  loading: true,
  failed: false
}

/** The table's rows, fifty at a time, following the server's own `next` link. */
export function useTrackingList(query: TrackingQuery) {
  const [state, setState] = useState<ListState>(STARTING)
  const [reloads, setReloads] = useState(0)
  // Only the newest request may write: an answer for filters the user already left is dropped.
  const latest = useRef(0)
  const key = JSON.stringify(query)

  useEffect(() => {
    const id = ++latest.current
    setState(STARTING)
    void getTracking({ ...query, page_size: SCROLL_PAGE_SIZE }).then(
      (result) => {
        if (id !== latest.current) return
        if (!result.ok || !isPage(result.data)) {
          setState({ ...STARTING, loading: false, failed: true })
          return
        }
        setState({
          items: result.data.results,
          count: result.data.count,
          next: result.data.next ?? null,
          retryNext: null,
          loading: false,
          failed: false
        })
      }
    )
    // `key` stands for `query`, which a caller rebuilds on every render.
  }, [key, reloads])

  const loadMore = useCallback(() => {
    const url = state.next ?? state.retryNext
    if (!url || state.loading) return
    const id = latest.current
    setState((current) => ({ ...current, loading: true }))
    void getTracking(extractParamsFromNextUrl<TrackingQuery>(url)).then(
      (result) => {
        if (id !== latest.current) return
        setState((current) =>
          result.ok && isPage(result.data)
            ? {
                ...current,
                items: [...current.items, ...result.data.results],
                count: result.data.count,
                next: result.data.next ?? null,
                retryNext: null,
                loading: false
              }
            : { ...current, next: null, retryNext: url, loading: false }
        )
      }
    )
  }, [state.next, state.retryNext, state.loading])

  const reload = useCallback(() => setReloads((count) => count + 1), [])
  return { ...state, loadMore, reload, generation: reloads }
}
